import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Radio from '@mui/material/Radio'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ManageHistoryOutlinedIcon from '@mui/icons-material/ManageHistoryOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined'
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined'
import CodeIcon from '@mui/icons-material/Code'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import type { MarkdownArtifactPanelData } from '@reachinsight-app/shared/src/components/R3Chat/types'
import { MarkdownArtifactPanel, MarkdownArtifactService } from './markdown-artifact'
import { MarkdownContent } from './MarkdownContent'
import type { SessionArtifact, PanelArtifactVersion } from './rosArtifacts'
import { artifactLabel, latestPublishedAt } from './rosArtifacts'
import { TypeTile } from '../pages/ArtifactsPage'
import { typeDef, relativeModified, latestVersion } from '../pages/artifactsModel'
import { ArtifactVersionsDrawer } from './ArtifactVersionsDrawer'
import { openSandboxedWebsitePreview } from '../services/sitePreviewWindow'
import { AppArtifactRenderer } from './AppArtifactRenderer'
import { ArtifactProtectControl } from './ArtifactProtectControl'
import { PublishedWebsiteFeedback } from './PublishedWebsiteFeedback'
import { artifactPublicationUrl } from './artifactPreviewUrl'

type Device = 'desktop' | 'mobile' | 'code'

const outlinedIconSx = { color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1 }
const filledMoreSx = { color: 'text.secondary', bgcolor: 'action.hover', borderRadius: 1 }
const shareIconSx = { bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1, '&:hover': { bgcolor: 'primary.dark' } }

interface ArtifactBody {
  html?: string
  [key: string]: unknown
}

interface ArtifactPreviewPanelProps {
  artifacts: SessionArtifact[]
  apiBaseUrl: string
  token: string | null
  onDownload: (a: SessionArtifact, v: PanelArtifactVersion) => void
  onClose: () => void
}

export function ArtifactPreviewPanel({ artifacts, apiBaseUrl, token, onDownload, onClose }: ArtifactPreviewPanelProps) {
  // null = list view. A single-artifact panel (the common case when opened from a table row) skips
  // the list entirely and lands straight in detail.
  const [activeId, setActiveId] = useState<string | null>(artifacts.length === 1 ? artifacts[0].id : null)
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [device, setDevice] = useState<Device>('desktop')
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null)
  const [body, setBody] = useState<{ status: 'loading' | 'ok' | 'error'; data?: ArtifactBody }>({ status: 'loading' })
  const [fullscreen, setFullscreen] = useState(false)
  const [markdownState, setMarkdownState] = useState<{ status: 'loading' | 'ok' | 'error'; data?: MarkdownArtifactPanelData }>({ status: 'loading' })
  const [versionsOpen, setVersionsOpen] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null)
  const [publishedFeedbackUrl, setPublishedFeedbackUrl] = useState<string | null>(null)

  useEffect(() => {
    if (artifacts.length === 1 && activeId !== artifacts[0].id) setActiveId(artifacts[0].id)
  }, [artifacts, activeId])

  const active = useMemo(() => artifacts.find(a => a.id === activeId) ?? null, [artifacts, activeId])
  const activeVersion = useMemo(() => {
    if (!active) return null
    if (selectedVersion == null) return latestVersion(active)
    return active.versions.find(v => v.version === selectedVersion) ?? latestVersion(active)
  }, [active, selectedVersion])
  const isMarkdown = active?.type === 'markdown'
  const isAppArtifact = active?.type === 'app-artifact'
  const markdownService = useMemo(() => new MarkdownArtifactService(apiBaseUrl, token), [apiBaseUrl, token])

  useEffect(() => {
    const path = active?.publication?.status === 'published' ? active.publication.path : undefined
    setPublishedUrl(artifactPublicationUrl(path, apiBaseUrl, window.location.origin))
    setPublishedFeedbackUrl(null)
    setPublishError(null)
  }, [activeId, active?.publication?.path, active?.publication?.status, apiBaseUrl])

  useEffect(() => {
    setSelectedVersion(null)
    setDevice('desktop')
    setFullscreen(false)
    setVersionsOpen(false)
  }, [activeId])

  useEffect(() => {
    if (!activeVersion || isMarkdown || isAppArtifact || !activeVersion.bodyR2Key) return
    let cancelled = false
    setBody({ status: 'loading' })
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    fetch(`${apiBaseUrl}/api/r3/artifact-body?key=${encodeURIComponent(activeVersion.bodyR2Key)}`, { credentials: 'include', headers })
      .then(res => { if (!res.ok) throw new Error(`artifact-body ${res.status}`); return res.json() })
      .then((data: ArtifactBody) => { if (!cancelled) setBody({ status: 'ok', data }) })
      .catch(() => { if (!cancelled) setBody({ status: 'error' }) })
    return () => { cancelled = true }
  }, [activeVersion, apiBaseUrl, token, isMarkdown, isAppArtifact])

  useEffect(() => {
    if (!active || !isMarkdown || !active.sessionId) return
    let cancelled = false
    setMarkdownState({ status: 'loading' })
    markdownService.read(active.sessionId)
      .then(data => { if (!cancelled) setMarkdownState({ status: 'ok', data }) })
      .catch(() => { if (!cancelled) setMarkdownState({ status: 'error' }) })
    return () => { cancelled = true }
  }, [active, isMarkdown, markdownService])

  const goBack = () => {
    if (artifacts.length <= 1) { onClose(); return }
    setActiveId(null)
  }

  if (!active) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '0.85rem' }}>Artifacts</Typography>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {artifacts.map(a => (
            <Box
              key={a.id}
              onClick={() => setActiveId(a.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, cursor: 'pointer',
                borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <TypeTile type={a.type} size={28} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{artifactLabel(a)}</Typography>
                <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>{relativeModified(latestPublishedAt(a))}</Typography>
              </Box>
              <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
            </Box>
          ))}
        </Box>
      </Box>
    )
  }

  const def = typeDef(active.type)

  // Websites carry real HTML we can write straight into the new tab; everything else (crosstabs,
  // surveys) has no HTML body, so fall back to a Blob of its raw text with the right mime type.
  // Markdown never populates `body` (its content comes from markdownState instead), so it gets its
  // own branch that renders the preview body client-side, same as MarkdownArtifactPanel's iframe.
  const openInNewTab = () => {
    if (isMarkdown) {
      if (device === 'code') {
        const raw = markdownState.data?.content ?? ''
        const url = URL.createObjectURL(new Blob([raw], { type: 'text/markdown;charset=utf-8' }))
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
        return
      }
      const html = markdownState.data?.previewHtml
      if (typeof html !== 'string') return
      const url = URL.createObjectURL(new Blob([
        `<!doctype html><html><head><meta charset="utf-8"></head><body style="font-family:system-ui,sans-serif;line-height:1.55;padding:24px;max-width:760px;margin:auto">${html}</body></html>`,
      ], { type: 'text/html' }))
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      return
    }
    if (typeof body.data?.html === 'string') {
      openSandboxedWebsitePreview(body.data.html)
      return
    }
    const raw = typeof body.data?.content === 'string' ? body.data.content : JSON.stringify(body.data ?? {})
    const mime = def.ext === '.csv' ? 'text/csv' : def.ext === '.md' ? 'text/markdown' : 'text/plain'
    const url = URL.createObjectURL(new Blob([raw], { type: `${mime};charset=utf-8` }))
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
  // Markdown reuses the survey bucket's Rendered/Raw toggle — same eye/code icon pattern, just
  // gated by isMarkdown instead of a second type check.
  const menuKind = active.type === 'website' ? 'website' : (active.type === 'survey' || isMarkdown) ? 'survey' : 'other'

  // Markdown has no bodyR2Key-addressed download; it exports through the artifact-lifecycle
  // engine instead, keyed by sessionId + revision.
  const downloadMarkdown = async () => {
    if (!markdownState.data || !active.sessionId) return
    const format = markdownState.data.format === 'text' ? 'text' : 'markdown'
    const { blob, fileName } = await markdownService.exportExact(active.sessionId, markdownState.data.revision, format)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url; anchor.download = fileName; anchor.click()
    URL.revokeObjectURL(url)
  }
  const handleDownload = () => {
    if (isMarkdown) { void downloadMarkdown(); return }
    if (activeVersion) onDownload(active, activeVersion)
  }

  const copyPublishedLink = async () => {
    if (!publishedUrl) return
    await navigator.clipboard.writeText(publishedUrl)
  }

  const publishAppArtifact = async () => {
    if (!active || !activeVersion || !isAppArtifact) return
    setPublishing(true)
    setPublishError(null)
    try {
      const headers: Record<string, string> = {'Content-Type': 'application/json'}
      if (token) headers.Authorization = `Bearer ${token}`
      const response = await fetch(
        `${apiBaseUrl}/api/r3/app-artifacts/${encodeURIComponent(active.id)}/publish`,
        {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({version: activeVersion.version}),
        },
      )
      const result = await response.json().catch(() => ({})) as {url?: string; error?: string}
      if (!response.ok || !result.url) throw new Error(result.error || `Publish failed (${response.status})`)
      setPublishedUrl(result.url)
      setPublishedFeedbackUrl(result.url)
    } catch (error) {
      setPublishError(error instanceof Error ? error.message : 'Could not publish App Artifact')
    } finally {
      setPublishing(false)
    }
  }

  return (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', position: 'relative',
      ...(fullscreen && { position: 'fixed', inset: 0, zIndex: 1300 }),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        {artifacts.length > 1 && (
          <Tooltip title="Back to artifacts">
            <IconButton size="small" onClick={goBack} sx={{ mr: 0.25 }}><ArrowBackIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        <TypeTile type={active.type} size={28} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: '0.04em', fontSize: '0.65rem' }}>
            {def.ext.replace('.', '').toUpperCase()}
          </Typography>
          <Typography noWrap sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
            {isMarkdown && active.sourceChatTitle ? active.sourceChatTitle : active.title}
          </Typography>
        </Box>

        {active.type !== 'crosstabs' && !isMarkdown && (
          <Tooltip title="Version history">
            <IconButton size="small" onClick={() => setVersionsOpen(o => !o)} sx={versionsOpen ? { ...outlinedIconSx, bgcolor: 'action.selected' } : outlinedIconSx}>
              <ManageHistoryOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isAppArtifact ? <>
          {publishedUrl && (
            <ArtifactProtectControl
              artifactId={active.id}
              initiallyProtected={active.isProtected}
              apiBaseUrl={apiBaseUrl}
              token={token}
            />
          )}
          <Tooltip title={publishedUrl ? 'Copy published link' : 'Publish to create a link'}>
            <span>
              <IconButton size="small" disabled={!publishedUrl} onClick={() => { void copyPublishedLink() }} sx={outlinedIconSx}>
                <LinkOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={publishing ? 'Publishing…' : 'Publish App Artifact'}>
            <span>
              <IconButton
                size="small"
                disabled={publishing || !activeVersion}
                onClick={() => { void publishAppArtifact() }}
                sx={shareIconSx}
              >
                {publishing ? <CircularProgress size={17} color="inherit" /> : <IosShareOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </> : <>
          <Tooltip title="Coming soon">
            <span><IconButton size="small" disabled sx={outlinedIconSx}><LinkOutlinedIcon fontSize="small" /></IconButton></span>
          </Tooltip>
          <Tooltip title={isMarkdown ? markdownState.data?.release.message ?? 'Coming soon' : 'Coming soon'}>
            <span><IconButton size="small" disabled sx={shareIconSx}><IosShareOutlinedIcon fontSize="small" /></IconButton></span>
          </Tooltip>
        </>}
        <Tooltip title={fullscreen ? 'Exit fullscreen' : 'Expand'}>
          <IconButton size="small" onClick={() => setFullscreen(f => !f)} sx={{ color: 'text.secondary' }}>
            {fullscreen ? <CloseFullscreenOutlinedIcon fontSize="small" /> : <OpenInFullOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="More">
          <IconButton size="small" onClick={e => setMoreAnchor(e.currentTarget)} sx={filledMoreSx}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
          {menuKind !== 'other' && (
            <Typography sx={{ px: 2, py: 0.5, fontSize: '0.7rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5 }}>View</Typography>
          )}
          {menuKind === 'website' && (
            <MenuItem onClick={() => { setDevice('desktop'); setMoreAnchor(null) }}>
              <ListItemIcon><Radio size="small" checked={device === 'desktop'} /></ListItemIcon>
              <ListItemIcon><DesktopWindowsIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Desktop</ListItemText>
            </MenuItem>
          )}
          {menuKind === 'website' && (
            <MenuItem onClick={() => { setDevice('mobile'); setMoreAnchor(null) }}>
              <ListItemIcon><Radio size="small" checked={device === 'mobile'} /></ListItemIcon>
              <ListItemIcon><PhoneIphoneIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Mobile</ListItemText>
            </MenuItem>
          )}
          {menuKind === 'website' && (
            <MenuItem onClick={() => { setDevice('code'); setMoreAnchor(null) }}>
              <ListItemIcon><Radio size="small" checked={device === 'code'} /></ListItemIcon>
              <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Code</ListItemText>
            </MenuItem>
          )}
          {menuKind === 'survey' && (
            <MenuItem onClick={() => { setDevice('desktop'); setMoreAnchor(null) }}>
              <ListItemIcon><Radio size="small" checked={device !== 'code'} /></ListItemIcon>
              <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Rendered</ListItemText>
            </MenuItem>
          )}
          {menuKind === 'survey' && (
            <MenuItem onClick={() => { setDevice('code'); setMoreAnchor(null) }}>
              <ListItemIcon><Radio size="small" checked={device === 'code'} /></ListItemIcon>
              <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Raw</ListItemText>
            </MenuItem>
          )}
          {menuKind !== 'other' && <Divider />}
          {!isAppArtifact && <MenuItem onClick={() => { openInNewTab(); setMoreAnchor(null) }}>
            <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Open in new tab</ListItemText>
          </MenuItem>}
          <MenuItem onClick={() => { handleDownload(); setMoreAnchor(null) }}>
            <ListItemIcon><DownloadOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download {def.ext}</ListItemText>
          </MenuItem>
        </Menu>
        <Tooltip title="Close">
          <IconButton size="small" onClick={goBack} sx={{ color: 'text.secondary' }}><CloseIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {publishError && (
        <Alert severity="error" onClose={() => setPublishError(null)} sx={{borderRadius: 0}}>
          {publishError}
        </Alert>
      )}

      {isAppArtifact ? (
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          {publishedFeedbackUrl && !publishError && (
            <PublishedWebsiteFeedback
              title="App Artifact published successfully"
              liveUrl={publishedFeedbackUrl}
              onDismiss={() => setPublishedFeedbackUrl(null)}
            />
          )}
          {activeVersion ? (
            <AppArtifactRenderer
              artifact={active}
              version={activeVersion}
              apiBaseUrl={apiBaseUrl}
              token={token}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                This App Artifact version is missing its runtime reference.
              </Typography>
            </Box>
          )}
        </Box>
      ) : isMarkdown ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {markdownState.status === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} sx={{ color: 'primary.main' }} /></Box>
          )}
          {markdownState.status === 'error' && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>Couldn't load this document.</Typography>
            </Box>
          )}
          {markdownState.status === 'ok' && markdownState.data && (
            device === 'code' ? (
              <Box component="pre" sx={{ m: 0, p: 3, height: '100%', overflow: 'auto', fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {markdownState.data.content}
              </Box>
            ) : (
              <MarkdownArtifactPanel
                state={markdownState.data}
                service={markdownService}
                onChange={data => setMarkdownState({ status: 'ok', data })}
              />
            )
          )}
        </Box>
      ) : (
      <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
        {body.status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} sx={{ color: 'primary.main' }} /></Box>
        )}
        {body.status === 'error' && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>Couldn't load this preview.</Typography>
          </Box>
        )}
        {body.status === 'ok' && device === 'code' && (() => {
          const source = typeof body.data?.html === 'string' ? body.data.html : body.data?.content
          return (
            <Box component="pre" sx={{ m: 0, p: 3, fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {typeof source === 'string' ? source : ''}
            </Box>
          )
        })()}
        {body.status === 'ok' && device !== 'code' && typeof body.data?.html === 'string' && (
          <Box sx={{
            width: '100%', height: '100%', display: 'flex', justifyContent: 'center',
            bgcolor: device === 'mobile' ? 'action.hover' : 'transparent',
            py: device === 'mobile' ? 2 : 0,
          }}>
            <iframe
              srcDoc={body.data.html}
              style={device === 'mobile'
                ? { width: 375, height: '100%', maxHeight: 812, border: '6px solid #555', borderRadius: 16, background: '#fff' }
                : { width: '100%', height: '100%', border: 'none' }}
              sandbox="allow-scripts"
              title={active.title}
            />
          </Box>
        )}
        {body.status === 'ok' && device !== 'code' && typeof body.data?.html !== 'string' && typeof body.data?.content === 'string' && (
          <Box sx={{ p: 3 }}><MarkdownContent content={body.data.content as string} /></Box>
        )}
        {body.status === 'ok' && device !== 'code' && typeof body.data?.html !== 'string' && typeof body.data?.content !== 'string' && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              Preview isn't available for this artifact yet, open it in chat, or download the file.
            </Typography>
          </Box>
        )}
      </Box>
      )}

      {versionsOpen && !isMarkdown && active.type !== 'crosstabs' && (
        <ArtifactVersionsDrawer
          kind="flat"
          versions={active.versions}
          currentVersion={activeVersion?.version ?? active.latestVersion}
          onClose={() => setVersionsOpen(false)}
          onView={v => setSelectedVersion(v.version)}
          onDownload={v => onDownload(active, v)}
        />
      )}

    </Box>
  )
}
